/**
 * Shutter/Venetian
 */

// prop-types is a library for typechecking of props
import React, { useState, memo } from "react";
import PropTypes from "prop-types";

// @mui material components
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import Icons from "components/CustomIcons";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import ModalDevice5 from "layouts/apartment/Devices/DeviceModals/ModalDevice5";
import colors from "assets/theme/base/colors";

// Material Dashboard 2 PRO React context
import { useMaterialUIController } from "context";

const { grey } = colors;

function CardDevice5({ device, sendValueServer, deviceState }) {
  const [open, setOpen] = useState(false);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { moveAddress, name, value, splitMove, upAddress, downAddress } = device;

  const deviceStateIcon = () => {
    if (value) {
      // eslint-disable-next-line no-nested-ternary
      return deviceState === 100
        ? "WindowShutter"
        : deviceState === 0
        ? "WindowShutterOpen"
        : "WindowShutterHalf";
    }
    return deviceState === 1 ? "WindowShutter" : "WindowShutterOpen";
  };

  return (
    <Card sx={{ height: "100%", overflow: "hidden" }}>
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{ overflow: "scroll" }}
      >
        <MDBox
          mt={3}
          sx={12}
          sm={8}
          style={{
            position: "absolute",
            width: "65vw",
            left: "50%",
            transform: "translate(-50%, 0%)",
          }}
        >
          <ModalDevice5
            device={device}
            sendValueServer={sendValueServer}
            deviceState={deviceState}
          />
        </MDBox>
      </Modal>
      <MDBox
        p={2}
        height="100%"
        variant="gradient"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        onClick={() => {
          setOpen(true);
        }}
        sx={({ palette: { background } }) => ({
          background: darkMode && background.card,
        })}
      >
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          lineHeight={1}
        >
          <Icons name={deviceStateIcon()} size={40} color={darkMode ? "white" : "black"} />
          <MDBox display="flex">
            <MDButton
              style={{
                padding: 0,
                marginRight: 8,
                backgroundColor: grey[300],
              }}
              onClick={(event) => {
                event.stopPropagation();
                if (!splitMove) {
                  sendValueServer(moveAddress, {
                    id: moveAddress,
                    value: 0,
                  });
                } else if (upAddress) {
                  sendValueServer(upAddress, {
                    id: upAddress,
                    value: 1,
                  });
                }
              }}
              iconOnly
            >
              <Icons name="ChevronUp" size={60} />
            </MDButton>
            <MDButton
              style={{
                padding: 0,
                backgroundColor: grey[300],
              }}
              onClick={(event) => {
                event.stopPropagation();
                if (!splitMove) {
                  sendValueServer(moveAddress, {
                    id: moveAddress,
                    value: 1,
                  });
                } else if (downAddress) {
                  sendValueServer(downAddress, {
                    id: downAddress,
                    value: 1,
                  });
                }
              }}
              iconOnly
            >
              <Icons name="ChevronDown" size={60} />
            </MDButton>
          </MDBox>
        </MDBox>
        <MDBox mt={1} lineHeight={1}>
          <MDTypography>{`${Math.round(deviceState)}%`}</MDTypography>
          <MDTypography variant="body2" textTransform="capitalize">
            {name?.toUpperCase()}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of CardDevice5
CardDevice5.defaultProps = {
  device: {},
  deviceState: 0,
};

// Typechecking props for the CardDevice5
CardDevice5.propTypes = {
  device: PropTypes.object,
  deviceState: PropTypes.number,
  sendValueServer: PropTypes.func.isRequired,
};

export default memo(CardDevice5, (prevProps, nextProps) => {
  const {
    device: { nodeId: nodeIdPrev },
    deviceState: deviceStatePrev,
  } = prevProps;
  const {
    device: { nodeId },
    deviceState,
  } = nextProps;

  return deviceStatePrev === deviceState && nodeIdPrev === nodeId;
});
